import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Fade,
  Link,
} from '@mui/material'
import {
  Visibility,
  VisibilityOff,
} from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useAppDispatch, useAppSelector } from '../../hooks/redux'
import { loginUser, clearError } from '../../store/slices/authSlice'
import bannerImage from '../../assets/images/banner.jpg'
import logoImage from '../../assets/images/logo.png'

// Validation schema
const loginSchema = yup.object({
  username: yup
    .string()
    .required('Username is required')
    .min(3, 'Username must be at least 3 characters'),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
})

interface LoginFormData {
  username: string
  password: string
}

export const LoginPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { isLoading, error, isAuthenticated } = useAppSelector((state) => state.auth)
  
  const [showPassword, setShowPassword] = useState(false)
  const [mounted, setMounted] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema) as any,
    defaultValues: {
      username: '',
      password: '',
    },
  })

  // Mount animation
  useEffect(() => {
    setMounted(true)
  }, [])

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as any)?.from?.pathname || '/dashboard'
      navigate(from, { replace: true })
    }
  }, [isAuthenticated, navigate, location])

  // Clear error when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError())
    }
  }, [dispatch])

  const onSubmit = async (data: LoginFormData) => {
    try {
      await dispatch(loginUser(data)).unwrap()
    } catch (error) {
      // Error is handled by Redux
    }
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#FFFFFF',
        margin: 0,
        padding: 0,
      }}
    >
      {/* Left Side - Login Form */}
      <Fade in={mounted} timeout={500}>
        <Box
          sx={{
            flex: { xs: 1, md: '0 0 50%' },
            width: { xs: '100%', md: '50%' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 2, sm: 3, md: 6 },
            py: { xs: 3, sm: 4, md: 6 },
            backgroundColor: '#241931ff',
            position: 'relative',
            zIndex: 2,
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: '480px',
              animation: mounted ? 'slideInLeft 0.5s ease-out' : 'none',
              '@keyframes slideInLeft': {
                from: {
                  opacity: 0,
                  transform: 'translateX(-30px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateX(0)',
                },
              },
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 4,
              }}
            >
              <Box
                component="img"
                src={logoImage}
                alt="Logo"
                sx={{
                  width: { xs: '160px', sm: '200px' },
                  height: 'auto',
                  objectFit: 'contain',
                }}
              />
            </Box>

            {/* Heading Section */}
            <Box sx={{ mb: { xs: 3, sm: 4 } }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '24px', sm: '28px', md: '32px' },
                  fontWeight: 700,
                  color: '#FFFFFF',
                  mb: 1,
                  lineHeight: 1.2,
                }}
              >
                Log In
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '14px', sm: '16px' },
                  color: '#FFFFFF',
                  lineHeight: 1.5,
                  opacity: 0.9,
                }}
              >
                Welcome back! Please enter your details
              </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
              <Fade in timeout={500}>
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: '12px',
                    backgroundColor: '#fef2f2',
                    color: '#991b1b',
                    border: '1px solid #fecaca',
                    '& .MuiAlert-icon': {
                      color: '#ef4444',
                    },
                  }}
                >
                  {error}
                </Alert>
              </Fade>
            )}

            {/* Login Form */}
            <Box component="form" onSubmit={handleSubmit(onSubmit as any)} noValidate>
              {/* Email Field */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  component="label"
                  sx={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#1a1a1a',
                    mb: 1,
                  }}
                >
                  Email
                </Typography>
                <Controller
                  name="username"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder="Enter your email"
                      error={!!errors.username}
                      helperText={errors.username?.message}
                      autoComplete="username"
                      autoFocus
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          minHeight: '48px',
                          borderRadius: '8px',
                          backgroundColor: '#f9fafb',
                          transition: 'all 0.2s ease-out',
                          '& fieldset': {
                            borderColor: '#e5e7eb',
                            borderWidth: '1px',
                          },
                          '&:hover': {
                            backgroundColor: '#f3f4f6',
                            '& fieldset': {
                              borderColor: '#d1d5db',
                            },
                          },
                          '&.Mui-focused': {
                            backgroundColor: '#FFFFFF',
                            '& fieldset': {
                              borderColor: '#667eea',
                              borderWidth: '2px',
                            },
                            boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
                          },
                          '&.Mui-error': {
                            '& fieldset': {
                              borderColor: '#ef4444',
                              borderWidth: '2px',
                            },
                          },
                        },
                        '& .MuiOutlinedInput-input': {
                          fontSize: '16px',
                          color: '#1a1a1a',
                          padding: '12px 16px',
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: '13px',
                          marginLeft: '4px',
                          marginTop: '6px',
                        },
                      }}
                    />
                  )}
                />
              </Box>

              {/* Password Field */}
              <Box sx={{ mb: 1 }}>
                <Typography
                  component="label"
                  sx={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#1a1a1a',
                    mb: 1,
                  }}
                >
                  Password
                </Typography>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={togglePasswordVisibility}
                              edge="end"
                              sx={{
                                color: '#9ca3af',
                                '&:hover': {
                                  color: '#667eea',
                                  backgroundColor: 'rgba(102, 126, 234, 0.08)',
                                },
                              }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      autoComplete="current-password"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          minHeight: '48px',
                          borderRadius: '8px',
                          backgroundColor: '#f9fafb',
                          transition: 'all 0.2s ease-out',
                          '& fieldset': {
                            borderColor: '#e5e7eb',
                            borderWidth: '1px',
                          },
                          '&:hover': {
                            backgroundColor: '#f3f4f6',
                            '& fieldset': {
                              borderColor: '#d1d5db',
                            },
                          },
                          '&.Mui-focused': {
                            backgroundColor: '#FFFFFF',
                            '& fieldset': {
                              borderColor: '#667eea',
                              borderWidth: '2px',
                            },
                            boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
                          },
                          '&.Mui-error': {
                            '& fieldset': {
                              borderColor: '#ef4444',
                              borderWidth: '2px',
                            },
                          },
                        },
                        '& .MuiOutlinedInput-input': {
                          fontSize: '16px',
                          color: '#1a1a1a',
                          padding: '12px 16px',
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: '13px',
                          marginLeft: '4px',
                          marginTop: '6px',
                        },
                      }}
                    />
                  )}
                />
              </Box>

              {/* Forgot Password Link */}
              <Box sx={{ textAlign: 'right', mb: 3 }}>
                <Link
                  href="#"
                  underline="none"
                  sx={{
                    fontSize: '14px',
                    color: '#667eea',
                    fontWeight: 400,
                    transition: 'all 0.15s ease-out',
                    '&:hover': {
                      textDecoration: 'underline',
                      color: '#5568d3',
                    },
                  }}
                >
                  forgot password?
                </Link>
              </Box>

              {/* Login Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{
                  minHeight: '48px',
                  borderRadius: '24px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#FFFFFF',
                  fontSize: '16px',
                  fontWeight: 500,
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                  transition: 'all 0.2s ease-out',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  '&:disabled': {
                    background: 'rgba(0, 0, 0, 0.12)',
                    boxShadow: 'none',
                    color: 'rgba(0, 0, 0, 0.26)',
                  },
                }}
              >
                {isLoading ? 'Signing In...' : 'Log in'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Fade>

      {/* Right Side - Visual Section */}
      <Box
        sx={{
          flex: { xs: 0, md: '0 0 50%' },
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          overflow: 'hidden',
        }}
      >
        {/* Decorative Blob Shapes */}
        <Box
          sx={{
            position: 'absolute',
            top: '-10%',
            right: '-5%',
            width: '40%',
            height: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
            filter: 'blur(40px)',
            animation: mounted ? 'fadeIn 0.9s ease-out 0.6s both' : 'none',
            '@keyframes fadeIn': {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '-10%',
            left: '-5%',
            width: '35%',
            height: '45%',
            background: 'rgba(255, 255, 255, 0.08)',
            borderRadius: '70% 30% 30% 70% / 70% 70% 30% 30%',
            filter: 'blur(40px)',
            animation: mounted ? 'fadeIn 0.9s ease-out 0.6s both' : 'none',
          }}
        />

        {/* Main Image */}
        <Box
          sx={{
            position: 'relative',
            maxWidth: '70%',
            maxHeight: '80%',
            animation: mounted ? 'fadeIn 0.7s ease-out 0.4s both' : 'none',
          }}
        >
          <Box
            component="img"
            src={bannerImage}
            alt="Garment factory workspace"
            sx={{
              width: '100%',
              height: 'auto',
              borderRadius: '24px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              border: '4px solid rgba(255, 255, 255, 0.2)',
              objectFit: 'contain',
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}
